/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { Copy } from 'lucide-react';
import { useState } from 'react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

import RowActions from './RowActions';

import { useGet, useMutate } from '@/net/commons';

export default function Keys() {
    const { data: keys } = useGet<Array<{ name: string; apiKeyPreview: string; id: number }>>(
        '/bff/organisation/keys',
        {
            revalidate: true,
        },
    );

    return (
        <div className="mt-10 space-y-4">
            <div className="rounded-xl border p-6 space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="font-semibold leading-none tracking-tight text-lg">Keys</h2>
                    <AddKeyTriggerAndModal />
                </div>
                {keys && (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[45%]">Key name</TableHead>
                                <TableHead className="w-[45%]">Key preview</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>{keys?.map((row) => <KeyRow key={row.id} row={row} />)}</TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}

const AddKeyTriggerAndModal = () => {
    const { trigger } = useMutate<{ key: string; id: number }>(`/bff/organisation/keys`, {
        revalidate: true,
        method: 'POST',
    });

    const [keyName, setKeyName] = useState('');
    const [keyExpiration, setKeyExpiration] = useState<string>('');
    const [keyData, setKeyData] = useState<{ key: string; id: number } | null>(null);
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button>Add Key</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{keyData ? 'Save your key' : 'Choose a name for your key'}</AlertDialogTitle>
                </AlertDialogHeader>
                {keyData ? (
                    <>
                        <div className="flex items-center rounded bg-gray-300 text-xs w-auto p-1 ps-2 gap-2 min-w-0 cursor-pointer">
                            <div
                                className="truncate"
                                onClick={() => {
                                    navigator.clipboard.writeText(keyData.key);
                                }}
                                title={keyData.key}
                            >
                                {keyData.key}
                            </div>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(keyData.key);
                                }}
                                className="bg-transparent hover:bg-transparent text-black border p-1"
                            >
                                <Copy size="14" />
                            </button>
                        </div>
                        <p>
                            This is your only chance to copy the key.
                            <br />
                            If you lose it, you will have to generate a new one.
                        </p>
                    </>
                ) : (
                    <>
                        <input
                            type="text"
                            className="w-full border border-gray-200 rounded p-2"
                            placeholder="Enter a name for your key"
                            value={keyName}
                            onChange={(e) => setKeyName(e.target.value)}
                            autoFocus
                        />
                        <div className="space-y-1">
                            <p className="text-sm">Expiration date:</p>
                            <input
                                type="date"
                                className="w-full border border-gray-200 rounded p-2"
                                value={keyExpiration}
                                onChange={(e) => setKeyExpiration(e.target.value)}
                            />
                            <p className="text-sm">By default, the key will never expire.</p>
                        </div>
                    </>
                )}
                <AlertDialogFooter>
                    {keyData ? (
                        <AlertDialogAction
                            className="bg-black hover:bg-black/90"
                            onClick={() => {
                                setKeyData(null);
                                setKeyName('');
                                setKeyExpiration('');
                            }}
                        >
                            Close
                        </AlertDialogAction>
                    ) : (
                        <>
                            <AlertDialogCancel
                                onClick={() => {
                                    setKeyName('');
                                    setKeyData(null);
                                    setKeyExpiration('');
                                }}
                            >
                                Cancel
                            </AlertDialogCancel>
                            <Button
                                className="bg-green-600 hover:bg-green-600/90"
                                onClick={async () => {
                                    const data = await trigger({
                                        name: keyName,
                                        ...(keyExpiration ? { expirationDate: new Date(keyExpiration) } : {}),
                                    });
                                    if (data) {
                                        setKeyData(data);
                                    }
                                }}
                            >
                                Get the key
                            </Button>
                        </>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

const KeyRow = ({ row }: { row: { name: string; apiKeyPreview: string; id: number } }) => {
    const [isClientSideDeleted, setIsClientSideDeleted] = useState(false);
    const { trigger } = useMutate('/bff/organisation/keys/:id', {
        revalidate: true,
        method: 'DELETE',
        onSuccess: () => {
            setIsClientSideDeleted(true);
        },
    });

    if (isClientSideDeleted) {
        return null;
    }

    return (
        <TableRow key={row.id}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.apiKeyPreview}</TableCell>
            <TableCell className="flex justify-end">
                <RowActions
                    alert={{
                        title: 'Delete key',
                        description: (
                            <>
                                Are you sure you want to delete the key <span className="font-bold">{row.name}</span>?
                            </>
                        ),
                    }}
                    onDelete={() => {
                        trigger({ idParam: row.id });
                    }}
                />
            </TableCell>
        </TableRow>
    );
};
